/**
 * Shared base64url decode helper (issue #613).
 *
 * `Buffer` is a Node global — it does NOT exist in browsers, and Vite does
 * not polyfill it. Any frontend code that needs to decode a base64url blob
 * built server-side (e.g. `encodeInvitePayload` in
 * `infrastructure/lambda/shared/invite-payload.ts`) must use `atob`, which
 * browsers provide natively but which only understands the standard
 * base64 alphabet (`+`/`/`, padded to a multiple of 4) — hence the
 * translate-and-pad step below. `atob` decodes each byte into one
 * Latin-1 character, so a `TextDecoder` pass is required afterwards to
 * correctly reassemble multi-byte UTF-8 sequences (e.g. non-ASCII names).
 *
 * Falls back to `Buffer` only when `atob` is unavailable (Node test
 * environments without the global, older Node versions).
 */

/**
 * Decode a base64url-encoded string into its original UTF-8 string.
 *
 * @param {string} value
 * @returns {string}
 * @throws if `value` is not a string or is not valid base64url.
 */
export function decodeBase64Url(value) {
  if (typeof value !== 'string') {
    throw new TypeError('decodeBase64Url expects a string')
  }
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)

  if (typeof atob === 'function') {
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  }
  return Buffer.from(padded, 'base64').toString('utf-8')
}
